import { Link } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Container, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Description, Login, Storefront } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

import { ROUTES } from '~/utils/constants';

const PORTAL_CARDS: Array<{
  title: string;
  description: string;
  href: string;
  icon: SvgIconComponent;
}> = [
  {
    title: 'Login',
    description: 'Sign in to the Tea Nursery Customer Portal',
    href: ROUTES.LOGIN,
    icon: Login,
  },
  {
    title: 'CN01 Form',
    description: 'Apply for a commercial V.P. tea nursery permit',
    href: ROUTES.PUBLIC_CN01_FORM,
    icon: Description,
  },
  {
    title: 'Supplier Form',
    description: 'Register as a supplier with TSHDA',
    href: ROUTES.PUBLIC_SUPPLIER_FORM,
    icon: Storefront,
  },
];

export function HomePage() {
  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: 'background.default',
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Box
          sx={(theme) => ({
            borderRadius: 2,
            overflow: 'hidden',
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
            px: { xs: 2, md: 4 },
            py: { xs: 2.5, md: 3.5 },
          })}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              component="img"
              src="/img/SL.png"
              alt="Sri Lanka national emblem"
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.9)',
                p: 1,
                objectFit: 'contain',
                flex: '0 0 auto',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>
                Ministry of Plantation and Community Infrastructure
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: 12, mt: 0.5 }}>
                Tea Small Holdings Development Authority
                <br />
                Tea Research Institute | Sri Lanka Tea Board | Department of Agriculture
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 2.5 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
              Tea Small Holdings Development Authority Public Portal
            </Typography>
            <Box sx={{ mt: 1.25, height: 1, bgcolor: 'rgba(255,255,255,0.25)' }} />
            <Typography sx={{ mt: 1.25, fontSize: 12, opacity: 0.95 }}>
              Select an option below to continue.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 2.5, md: 3.5 },
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          {PORTAL_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.href}
                variant="outlined"
                sx={(theme) => ({
                  borderRadius: 2.5,
                  height: '100%',
                  borderColor: alpha(theme.palette.divider, 0.9),
                  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.light, 0.14),
                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.dark, 0.12)}`,
                  },
                })}
              >
                <CardActionArea
                  component={Link}
                  to={card.href}
                  sx={{ height: '100%', p: 2.5, borderRadius: 2.5, alignItems: 'stretch' }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ color: 'primary.main' }}>
                        <Icon sx={{ fontSize: 34 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{card.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, fontSize: 13 }}>
                          {card.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
